import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db74ps7799gd4v2febug";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [values, setValues] = useState({ name: "", contact: "", message: "" });

  const handleChange = (key: keyof typeof values, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot check - bots fill hidden fields
    const honey = String(formData.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      setValues({ name: "", contact: "", message: "" });
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const name = String(formData.get("name") ?? "").trim();
    const contact = String(formData.get("contact") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !contact || !message) {
      setStatus("error");
      setFormError("请完整填写姓名、联系方式与留言内容。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容不能超过 500 个字符。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText ||
        "";

      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setValues({ name: "", contact: "", message: "" });
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试，或直接通过电话与邮箱联系我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  const inputClass =
    "w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 placeholder:text-foreground-400";

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="contact-form rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8"
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">在线留言</h2>
      <p className="mt-2 text-sm text-foreground-600">
        有任何想法或合作意向，欢迎给我们留言，我们会尽快回复。
      </p>

      <div className="mt-6 space-y-5">
        <div>
          <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground-800">
            姓名 <span className="text-accent-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={values.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="请输入您的姓名"
            className={inputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="contact" className="mb-2 block text-sm font-medium text-foreground-800">
            联系方式 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact"
            name="contact"
            type="text"
            value={values.contact}
            onChange={(e) => handleChange("contact", e.target.value)}
            placeholder="请输入手机号或邮箱"
            className={inputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="message" className="mb-2 block text-sm font-medium text-foreground-800">
            留言内容 <span className="text-accent-600">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={5}
            maxLength={500}
            value={values.message}
            onChange={(e) => handleChange("message", e.target.value)}
            placeholder="请输入您的留言，最多 500 字"
            className={`${inputClass} resize-none`}
            required
          />
          <p className="mt-1.5 text-right text-xs text-foreground-400">{values.message.length}/500</p>
        </div>

        <input
          type="text"
          name="website_alt"
          className="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        {status === "error" && formError && (
          <div className="flex items-start gap-2 rounded-lg border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
            <i className="ri-error-warning-line mt-0.5" />
            <span>{formError}</span>
          </div>
        )}

        {status === "success" && (
          <div className="flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
            <i className="ri-checkbox-circle-line mt-0.5" />
            <span>提交成功，感谢您的留言，我们会尽快与您联系。</span>
          </div>
        )}

        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line" />
              提交留言
            </>
          )}
        </button>
      </div>
    </form>
  );
}